import React, { useState, useEffect } from 'react';
import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Label } from 'recharts';
import styles from "./verticalBarGraph.module.css";

const LineGraphOEE = ({ data }) => {
    const [allOEEDetails, setAllOEEDetails] = useState(data);
    const [activeLine, setActiveLine] = useState(null);
    const [tooltipTimeout, setTooltipTimeout] = useState(null);
    const [colorCode, setColorCode] = useState([]);

    const transformedData = Object.entries(allOEEDetails[0]?.data || {}).map(([week, value]) => {
        let finalobj = { weekLabel: week }
        for (let val of allOEEDetails) {
            finalobj = { ...finalobj, [val.name]: val.data[week], [`${val.name}_OEE`]: val.dataOEEpercent[week] * 100 }
        }
        return finalobj
    });

    const maxExcess = (arr) => {
        const rawMax = arr.reduce((max, item) => {
            const values = Object.values(item)
                .map(val => Number(val))
                .filter(val => !isNaN(val));
            return Math.max(max, ...values);
        }, 0);

        if (rawMax <= 0) return 0;

        // Find order of magnitude (like 1000s, 10000s)
        const magnitude = Math.pow(10, Math.floor(Math.log10(rawMax)));
        // Round up to next "nice" step
        const roundedMax = Math.ceil(rawMax / magnitude) * magnitude;

        return roundedMax;
    };

    const key = allOEEDetails.map(val => val.name);

    useEffect(() => {
        if (data && data.length > 0) {
            const keys = data.map(val => val.name);
            const newColorCode = keys.map(() =>
                `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')}`
            );
            setColorCode(newColorCode);
        }
    }, [data]);

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length && activeLine) {
            const activePayload = payload.find(p => p.dataKey === activeLine);
            if (!activePayload) return null;

            const oeeValue = activePayload.payload[`${activeLine}_OEE`];
            const weekValue = activePayload.payload[activeLine]; // This gets the actual week value
            return (
                <div className={styles.customTooltip}>
                    <p className={styles.tooltipItem}>
                        <span style={{ fontWeight: 600 }}>{activeLine} Capacity Master OEE% : </span>
                        {oeeValue}%
                    </p>
                    <p className={styles.tooltipItem}>
                        <span style={{ fontWeight: 600 }}>{label} : </span>
                        {parseFloat(weekValue)?.toFixed(1)}%
                    </p>
                </div>
            );
        }
        return null;
    };

    const CustomLegend = ({ payload }) => {
        return (
            <div style={{ display: 'flex', justifyContent: 'left', padding: '10px', flexWrap: 'wrap' }}>
                {payload.map((entry, index) => {
                    const { value, color } = entry;
                    if (!value.includes('_OEE')) {
                        return (
                            <span
                                key={`item-${index}`}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: '0 10px',
                                    cursor: 'pointer',
                                    opacity: activeLine && activeLine !== value ? 0.5 : 1
                                }}
                                onClick={() => setActiveLine(activeLine === value ? null : value)}
                            >
                                <span
                                    style={{
                                        backgroundColor: color,
                                        width: '6px',
                                        height: '12px',
                                        display: 'inline-block',
                                        borderRadius: '2px',
                                    }}
                                />
                                <span style={{ color: '#4D4D4F' }}>{value}</span>
                            </span>
                        );
                    }
                    return null;
                })}
            </div>
        );
    };

    const handleMouseEnter = (lineKey) => {
        // Clear any existing timeout
        if (tooltipTimeout) {
            clearTimeout(tooltipTimeout);
        }

        // Set new timeout to prevent flickering
        setTooltipTimeout(setTimeout(() => {
            setActiveLine(lineKey);
        }, 100));
    };

    const handleMouseLeave = () => {
        if (tooltipTimeout) {
            clearTimeout(tooltipTimeout);
        }
        setActiveLine(null);
    };

    const legendStyle = {
        fontSize: '10px',
        paddingTop: '15px',
        color: '#4D4D4F',
    };

    const formatYAxisTick = (value) => {
        return `${value}%`;
    };

    useEffect(() => {
        setAllOEEDetails(data);
        return () => {
            if (tooltipTimeout) {
                clearTimeout(tooltipTimeout);
            }
        };
    }, [data]);

    return (
        <ResponsiveContainer width="100%" height={350}>
            <ComposedChart
                data={transformedData}
                margin={{ top: 10, right: 30, bottom: 10, left: 0 }}
                onMouseLeave={handleMouseLeave}
            >
                <CartesianGrid strokeDasharray="3 3" vertical={false} horizontal={true} />
                <XAxis
                    dataKey="weekLabel"
                    tickLine={false}
                    axisLine={false}
                    className={styles.BarLabel}
                />
                <YAxis
                    // domain={[0, Math.round(maxExcess(transformedData) * 1)]}
                    domain={[0, maxExcess(transformedData)]}
                    tickLine={false}
                    axisLine={false}
                    className={styles.BarLabel}
                    padding={{ left: 0, right: 0 }}
                    tickFormatter={formatYAxisTick}
                >
                    <Label
                        value="OEE (percentage)"
                        angle={-90}
                        offset={15}
                        position="insideLeft"
                        style={{ textAnchor: "middle" }}
                        className={styles.labelListOR}
                    />
                </YAxis>
                <Tooltip
                    content={<CustomTooltip />}
                    wrapperStyle={{
                        fontSize: '12px',
                        padding: '6px',
                    }}
                />
                <Legend
                    verticalAlign="bottom"
                    align="left"
                    wrapperStyle={legendStyle}
                    content={<CustomLegend />}
                />
                {key.map((item, index) => (
                    <Line
                        key={item}
                        type="monotone"
                        dataKey={item}
                        stroke={colorCode[index]}
                        fill={colorCode[index]}
                        strokeWidth={activeLine === item ? 3 : 1}
                        activeDot={{
                            r: 6,
                            onMouseOver: () => handleMouseEnter(item),
                            onMouseLeave: handleMouseLeave
                        }}
                        onMouseOver={() => handleMouseEnter(item)}
                        opacity={activeLine && activeLine !== item ? 0.5 : 1}
                    />
                ))}
            </ComposedChart>
        </ResponsiveContainer>
    );
};

export default LineGraphOEE;